import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useAppContext } from '@prisjakt/ui-app-context';

import HeaderBlock from 'src/areas/product-collection/components/header-block';
import { SearchResultsNavigation } from '../navigation';
import { NoSearchResults } from 'src/components/no-search-results';
import { ProductResults } from './product-results';
import { DidYouMean } from '../did-you-mean';
import { PageContent } from 'src/components/page';
import { CategoryResults } from './category-results';
import { BrandResults } from './brand-results';
import { ShopResults } from './shop-results';
import { TestAndGuideResults } from './test-and-guide-results';
import { KeywordSuggestions } from '../keyword-suggestions';
import { ScrollElement } from 'src/components/scroll/components/element';
import ProductScrollTopButton from 'src/areas/product/components/product-scroll-top-button';

import { useEffectOnMount } from 'src/hooks/useEffectOnMount';
import {
  useSearchResultLayoutExperiment,
  SearchResultLayoutExperimentType,
  usePanoramaAdInContentExperiment,
  PanoramaAdInContentExperimentType,
} from 'src/hooks/experiments';

import { trackSearchNoResultViewed } from '../../tracking';

import { TabAnchor } from '../navigation/types';
import { SearchPageQuery, SearchFacetList, Product, Offer } from 'src/types';

import { NEW_SEARCH_INDEX_MARKETS } from '../../constants';

import * as S from './styles';

const SHOW_ALL = true;

type Props = {
  data: SearchPageQuery;
};

enum SearchProductFacet {
  CATEGORY_ID = 'category_id',
  BRAND_ID = 'brand_id',
  LOWEST_PRICE = 'lowest_price',
  USER_RATING = 'user_rating',
}

const SearchPage = (props: Props) => {
  const { data } = props;

  const dispatch = useDispatch();

  const { t, market } = useAppContext();

  const searchResultLayoutExperiment = useSearchResultLayoutExperiment();
  const panoramaAdInContentExperimentVariant = usePanoramaAdInContentExperiment();

  const [searchResultLayout, setSearchResultLayout] = useState<
    SearchResultLayoutExperimentType
  >(false);
  const [activeTab, setActiveTab] = useState('');
  const [panoramaAdInContentVariant, setPanoramaAdInContentVariant] = useState<
    PanoramaAdInContentExperimentType
  >(false);

  const location = useLocation();

  const searchContainer = useRef<HTMLDivElement>(null);

  useEffectOnMount(() => {
    dispatch({
      type: 'ADS_VIEW_UPDATE',
      view: `search`,
    });
  });

  useEffect(() => {
    setPanoramaAdInContentVariant(panoramaAdInContentExperimentVariant);
  }, [panoramaAdInContentExperimentVariant]);

  useEffect(() => {
    setSearchResultLayout(searchResultLayoutExperiment);
  }, [searchResultLayoutExperiment]);

  useEffect(() => {
    if (location.pathname === '/search' && activeTab !== location.hash) {
      setActiveTab(location.hash.length === 0 ? TabAnchor.ALL : location.hash);
    }
  }, [location.hash]);

  useEffect(() => {
    // When performing a new search..
    if (data.search?.query?.length) {
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'auto' });

      setActiveTab(location.hash.length > 0 ? location.hash : TabAnchor.ALL);
    }
  }, [data.search?.query]);

  const getCategoriesFromProductsFacets = () => {
    const productCategoryFacet = data.search?.resources.products.facets?.filter(
      facet => facet?.id === SearchProductFacet.CATEGORY_ID
    ) as SearchFacetList[];
    return productCategoryFacet?.length && productCategoryFacet[0]
      ? productCategoryFacet[0].buckets
      : [];
  };

  const categories = getCategoriesFromProductsFacets();
  const newSearch = NEW_SEARCH_INDEX_MARKETS.includes(market.code);

  if ((newSearch && !data.newSearch) || (!newSearch && !data.search))
    return null;

  const { products, brands, stores } = data.search?.resources || {};
  const { didYouMean, popularSearches, keywordSuggestions: keywords } = data;

  const productsFromNewSearch = data.newSearch?.results?.products;
  const categoriesFromNewSearch = data.newSearch?.results?.categories;
  const brandsFromNewSearch = data.newSearch?.results?.brands;
  const shopsFromNewSearch = data.newSearch?.results?.shops;
  const expertContent = data.newSearch?.results?.articlesResult?.items;

  const expertItems = [
    ...(expertContent?.guides.length ? expertContent.guides : []),
    ...(expertContent?.tests.length ? expertContent.tests : []),
    ...(expertContent?.toplists.length ? expertContent.toplists : []),
    ...(expertContent?.articles.length ? expertContent.articles : []),
  ];

  const productsCount = newSearch
    ? productsFromNewSearch?.pageInfo?.total || 0
    : products?.pageInfo?.total || 0;
  const categoriesCount = newSearch
    ? categoriesFromNewSearch?.pageInfo?.total || 0
    : categories?.length || 0;
  const storesCount = newSearch
    ? shopsFromNewSearch?.pageInfo?.total || 0
    : stores?.nodes.length || 0;
  const brandsCount = newSearch
    ? brandsFromNewSearch?.pageInfo?.total || 0
    : brands?.nodes.length || 0;
  const testsAndGuidesCount = newSearch ? expertItems.length : 0;

  const noResults = !newSearch
    ? !products?.nodes?.length &&
      !categories?.length &&
      !brands?.nodes?.length &&
      !stores?.nodes?.length
    : !productsFromNewSearch?.nodes?.length &&
      !categoriesFromNewSearch?.nodes.length &&
      !brandsFromNewSearch?.nodes.length &&
      !shopsFromNewSearch?.nodes?.length &&
      !expertItems?.length;

  const brandNodes = newSearch
    ? brandsFromNewSearch?.nodes || []
    : brands?.nodes || [];

  const searchQuery =
    (data.newSearch && data.newSearch.query) ||
    (data.search && data.search.query) ||
    '';

  const useWidePageContent =
    searchResultLayout === 'wide' ||
    searchResultLayout === 'wide and without panorama';
  const useTrailingSkyscraperAndDisableTopPanoramaAd =
    panoramaAdInContentVariant ===
      'trailing skyscraper without top panorama ad' ||
    panoramaAdInContentVariant ===
      'trailing skyscraper without top panorama ad with panorama ad in content';

  const handleOnClick = (tab: string) => {
    window.scrollTo({
      top: searchContainer.current?.offsetTop || 0,
      behavior: 'auto',
    });

    setActiveTab(tab);
  };

  const isTabVisible = (currentTab: string) => {
    if (activeTab.length === 0) return SHOW_ALL;
    return activeTab === currentTab;
  };

  return (
    <PageContent
      withAds
      data-test="SearchResultsPage"
      isWide={useWidePageContent}
      trailingSkyscraper={useTrailingSkyscraperAndDisableTopPanoramaAd}
      {...(useTrailingSkyscraperAndDisableTopPanoramaAd && {
        topPlacements: [],
      })}
    >
      <ScrollElement name="Top" />
      <HeaderBlock
        title={
          noResults
            ? t('search', 'no-search-results.title', { query: searchQuery })
            : t('search', 'search-results.title', {
                query: searchQuery,
              })
        }
      />
      {didYouMean?.length ? (
        <S.ModuleWrapper>
          <DidYouMean
            queries={didYouMean.map(s => s.text)}
            searchQuery={searchQuery}
          />
        </S.ModuleWrapper>
      ) : null}

      {Boolean(keywords?.length) && <KeywordSuggestions keywords={keywords} />}

      {noResults ? (
        <NoSearchResults
          onRender={() => trackSearchNoResultViewed(searchQuery)}
          popularSearches={popularSearches}
        />
      ) : (
        <>
          <SearchResultsNavigation
            activeTab={activeTab}
            brandsCount={brandsCount}
            categoriesCount={categoriesCount}
            onClick={handleOnClick}
            productsCount={productsCount}
            storesCount={storesCount}
            testsAndGuidesCount={testsAndGuidesCount}
          />

          <S.SearchResults
            gap={activeTab.length > 0 && activeTab !== TabAnchor.ALL ? 0 : 2}
          >
            <CategoryResults
              active={activeTab === TabAnchor.CATEGORIES}
              visible={isTabVisible(TabAnchor.CATEGORIES)}
              categories={
                newSearch ? categoriesFromNewSearch?.nodes : categories
              }
              count={categoriesCount}
              newSearch={newSearch}
              query={searchQuery}
              wideContent={useWidePageContent}
            />

            <ProductResults
              visible={isTabVisible(TabAnchor.PRODUCTS)}
              facets={newSearch ? data.newSearch?.filters : products?.facets}
              pageInfo={
                newSearch ? productsFromNewSearch?.pageInfo : products?.pageInfo
              }
              products={
                newSearch
                  ? (productsFromNewSearch?.nodes as (Product | Offer)[]) || []
                  : products?.nodes || []
              }
              productsCount={productsCount}
              sort={products?.pageInfo?.sort}
              query={searchQuery}
              newSearch={newSearch}
              isWide={useWidePageContent}
              embedPanoramaAd={
                panoramaAdInContentVariant ===
                'trailing skyscraper without top panorama ad with panorama ad in content'
              }
            />

            <TestAndGuideResults
              active={activeTab !== TabAnchor.ALL}
              count={testsAndGuidesCount}
              items={expertItems}
              query={searchQuery}
              visible={isTabVisible(TabAnchor.TESTS)}
              wideContent={useWidePageContent}
            />

            <BrandResults
              active={activeTab !== TabAnchor.ALL}
              brands={brandNodes}
              count={brandsCount}
              query={searchQuery}
              visible={isTabVisible(TabAnchor.BRANDS)}
              wideContent={useWidePageContent}
            />

            <ShopResults
              active={activeTab !== TabAnchor.ALL}
              count={storesCount}
              query={searchQuery}
              shops={
                newSearch
                  ? shopsFromNewSearch?.nodes || []
                  : stores?.nodes || []
              }
              visible={isTabVisible(TabAnchor.STORES)}
              wideContent={useWidePageContent}
            />
          </S.SearchResults>
        </>
      )}
      <ProductScrollTopButton />
    </PageContent>
  );
};

export default SearchPage;
